import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  Building2,
  Calculator,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Minus,
  RefreshCw,
  SlidersHorizontal,
  Sparkles,
  Tag,
  Trophy,
  X,
  type LucideIcon,
} from "lucide-react";

import BuyerPreferencesModal from "@/components/BuyerPreferencesModal";
import { api, assetUrl } from "@/lib/api";
import { buyBoxTagIcon } from "@/lib/buyboxIcons";
import { cn } from "@/lib/utils";
import { orgScopedPath } from "@/lib/orgDoor";
import { useAuth } from "@/contexts/AuthContext";

/**
 * AI Deal Scoring — buyer-facing page that scores live listings against the
 * buyer's saved BuyBox. Data comes from GET /business-listings/deal-scoring:
 * the buyer's top-fit active listings, each with an overall fit score,
 * per-dimension metrics, BuyBox signals, and heuristic insights.
 */

type SignalKind = "match" | "watch" | "miss";
type InsightKind = "positive" | "warning" | "negative";

interface DealSignal {
  kind: SignalKind;
  text: string;
}

interface DealMetric {
  key: string;
  label: string;
  value: number;
}

interface DealInsight {
  kind: InsightKind;
  text: string;
}

interface ScoredDeal {
  listing_id: string;
  reference_code: string | null;
  title: string;
  location: string | null;
  category: string | null;
  listing_image_url: string | null;
  asking_price: number | null;
  revenue: number | null;
  ebitda: number | null;
  fit_score: number;
  metrics: DealMetric[];
  signals: DealSignal[];
  insights: DealInsight[];
}

interface BuyBoxSummary {
  budget_label: string | null;
  categories: string[];
  location: string | null;
  sde_label: string | null;
  funding: string[];
}

interface DealScoringResponse {
  has_preferences: boolean;
  buybox: BuyBoxSummary | null;
  deals: ScoredDeal[];
}

function ratingLabel(score: number): string {
  if (score >= 90) return "Excellent Match";
  if (score >= 80) return "Great Match";
  if (score >= 70) return "Good Match";
  return "Fair Match";
}

/** emerald / amber / rose by threshold — shared across the ring and metric tiles.
    500-weight reads well on both the light and dark card surfaces. */
function scoreTextColor(score: number): string {
  if (score >= 85) return "text-emerald-500";
  if (score >= 70) return "text-amber-500";
  return "text-rose-500";
}

function ringStroke(score: number): string {
  if (score >= 85) return "#22c55e";
  if (score >= 70) return "#f59e0b";
  return "#ef4444";
}

/** Theme-gold chip for the BL Score badge on the match carousel and the
    deal-switcher popup — matches the category pill, no status tint. */
const SCORE_CHIP_CLASSES = "border-primary/30 bg-card/90 text-primary";

function fmtMoney(n: number): string {
  if (n >= 1_000_000) {
    const m = n / 1_000_000;
    return `$${Number.isInteger(m) ? m : parseFloat(m.toFixed(2))}M`;
  }
  if (n >= 1_000) return `$${Math.round(n / 1000)}K`;
  return `$${Math.round(n)}`;
}

/* Signal icons sit on the gold `bg-primary` banner in both themes, so they use
   fixed solid chips (not theme-remapped pastels) to stay legible on gold. */
const SIGNAL_STYLES: Record<SignalKind, { Icon: LucideIcon; chip: string }> = {
  match: { Icon: Check, chip: "bg-emerald-600" },
  watch: { Icon: Minus, chip: "bg-amber-500" },
  miss: { Icon: X, chip: "bg-rose-600" },
};

/* Translucent status tints instead of solid pastels, so the rows sit naturally
   on both the light and dark card surfaces; the text stays theme foreground. */
const INSIGHT_STYLES: Record<InsightKind, { Icon: LucideIcon; row: string; icon: string }> = {
  positive: {
    Icon: Check,
    row: "border-emerald-500/30 bg-emerald-500/10 text-foreground/90",
    icon: "text-emerald-500",
  },
  warning: {
    Icon: Minus,
    row: "border-amber-500/30 bg-amber-500/10 text-foreground/90",
    icon: "text-amber-500",
  },
  negative: {
    Icon: X,
    row: "border-rose-500/30 bg-rose-500/10 text-foreground/90",
    icon: "text-rose-500",
  },
};

function FitScoreRing({ score }: { score: number }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - score / 100);
  return (
    <div className="relative flex h-[104px] w-[104px] shrink-0 items-center justify-center">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
        {/* Light track — the ring sits on the dark hero-shell banner */}
        <circle cx="50" cy="50" r={radius} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="8" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={ringStroke(score)}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground">
        <span className="text-2xl font-extrabold leading-none">{score}</span>
        <span className="mt-1 text-[0.55rem] font-bold uppercase tracking-[0.12em] opacity-70">
          BL Score
        </span>
      </div>
    </div>
  );
}

function PageHeader() {
  return (
    // Same Dark · Gold hero as Deal CRM and the other interior pages.
    <section className="public-hero-shell mb-6 rounded-[1.85rem] px-6 py-7 shadow-glow sm:px-8">
      <div className="public-grid-lattice absolute inset-0 opacity-45" />
      <div className="absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
      <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="public-pill">
            <Sparkles className="h-3.5 w-3.5" />
            AI Deal Matching & Scoring
          </div>
          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-4xl">
            AI <span className="text-gradient">Deal Matching & Scoring</span>
          </h1>
          <p className="mt-2 max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg">
            BL Scores calculated against your saved BuyBox in real-time.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/15 px-3 py-1.5 text-sm font-bold text-primary">
            <Trophy className="h-4 w-4" />
            Top 10 Deals
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/30 bg-violet-500/15 px-3 py-1.5 text-sm font-bold text-violet-400">
            <Sparkles className="h-4 w-4" />
            Powered by AI
          </span>
        </div>
      </div>
    </section>
  );
}

/** Chips summarising the BuyBox the scores are computed against. */
function BuyBoxChips({ buybox, onEdit }: { buybox: BuyBoxSummary; onEdit: () => void }) {
  const chips: string[] = [
    ...(buybox.budget_label ? [buybox.budget_label] : []),
    ...buybox.categories,
    // location is a ", "-joined list once a buyer targets several places —
    // one chip each reads far better than a single sprawling pill.
    ...(buybox.location ? buybox.location.split(", ").filter(Boolean) : []),
    ...(buybox.sde_label ? [`${buybox.sde_label}+ SDE`] : []),
  ];
  return (
    <div className="mb-5">
      {/* Plain label on its own row; the chips (gold-gradient/dark-ink, same
          fill as the primary buttons) sit underneath it. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Your BuyBox
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
        >
          <SlidersHorizontal className="h-3.5 w-3.5" />
          Edit BuyBox
        </button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {chips.map((chip) => {
          const Icon = buyBoxTagIcon(chip);
          return (
            <span
              key={chip}
              className="inline-flex items-center gap-1.5 rounded-full bg-gold-gradient px-3 py-1.5 text-sm font-semibold text-primary-foreground shadow-sm"
            >
              <Icon className="h-3.5 w-3.5 text-primary-foreground" />
              {chip}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Swipeable row of the other top matches, styled like the listing-detail
    "Explore More Opportunities" carousel. Tapping a card scores it in the
    panel above; "Open Deal" goes straight to the listing. */
function OtherMatchesCarousel({
  deals,
  onSelect,
}: {
  deals: ScoredDeal[];
  onSelect: (id: string) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scroll = (dir: -1 | 1) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <section className="mt-8">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-foreground">
            <Sparkles className="h-6 w-6 shrink-0 text-primary" />
            Your Other {deals.length} AI Deal Matches
          </h2>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Tap a deal to see its score breakdown, or open the listing.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => scroll(-1)}
            aria-label="Previous deals"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border/80 bg-card text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/55"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => scroll(1)}
            aria-label="Next deals"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-border/80 bg-card text-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/55"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="-mx-1 flex snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-3"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {deals.map((d) => {
          const img = d.listing_image_url ? assetUrl(d.listing_image_url) : null;
          return (
            <div
              key={d.listing_id}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(d.listing_id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onSelect(d.listing_id);
                }
              }}
              className="group flex w-[260px] shrink-0 cursor-pointer snap-start flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md sm:w-[280px]"
            >
              <div className="relative h-40 w-full overflow-hidden bg-muted">
                {img ? (
                  <img
                    src={img}
                    alt={d.title}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Building2 className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                )}
                {d.category && (
                  <span className="absolute left-3 top-3 max-w-[60%] truncate rounded-full border border-primary/20 bg-card/90 px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                    {d.category}
                  </span>
                )}
                <span
                  className={cn(
                    "absolute right-3 top-3 flex flex-col items-center rounded-lg border px-2 py-1 backdrop-blur-sm",
                    SCORE_CHIP_CLASSES
                  )}
                >
                  <span className="text-base font-extrabold leading-none tabular-nums">{d.fit_score}</span>
                  <span className="mt-0.5 text-[0.5rem] font-bold uppercase tracking-[0.1em] opacity-80">BL Score</span>
                </span>
              </div>
              <div className="flex flex-1 flex-col gap-2 p-4">
                <h3 className="line-clamp-2 font-display text-base font-bold leading-snug text-foreground transition-colors group-hover:text-primary">
                  {d.title}
                </h3>
                <div className="flex items-center justify-between gap-2 text-sm">
                  {d.asking_price != null ? (
                    <span className="shrink-0 font-extrabold text-foreground">{fmtMoney(d.asking_price)}</span>
                  ) : (
                    <span />
                  )}
                  {d.location && (
                    <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{d.location}</span>
                    </span>
                  )}
                </div>
                <div className="mt-auto flex items-center gap-2 pt-2">
                  <span className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gold-gradient px-3 py-2 text-xs font-semibold text-primary-foreground">
                    <Sparkles className="h-3.5 w-3.5" />
                    View Score
                  </span>
                  <Link
                    to={`/listings/${d.listing_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border/80 bg-card px-3 py-2 text-xs font-semibold text-foreground transition-colors hover:border-primary/30 hover:bg-accent/55"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open Deal
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function AiDealScoringPage() {
  // Workspace links carry the user's organization prefix.
  const { user } = useAuth();
  const [data, setData] = useState<DealScoringResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const scorecardRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<DealScoringResponse>("/business-listings/deal-scoring?limit=10");
      setData(res);
      setActiveId((prev) =>
        prev && res.deals.some((d) => d.listing_id === prev) ? prev : res.deals[0]?.listing_id ?? null
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load deal scores");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Close the deal-switcher popup on Escape.
  useEffect(() => {
    if (!switcherOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSwitcherOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [switcherOpen]);

  const deals = data?.deals ?? [];
  const deal = deals.find((d) => d.listing_id === activeId) ?? deals[0];

  const selectDeal = (id: string) => {
    setActiveId(id);
    setSwitcherOpen(false);
  };

  // A carousel pick swaps the scorecard above — scroll it back into view.
  const selectFromCarousel = (id: string) => {
    selectDeal(id);
    scorecardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const facts = deal
    ? [
        ...(deal.location ? [{ Icon: MapPin, text: deal.location }] : []),
        ...(deal.asking_price != null ? [{ Icon: Tag, text: `Asking ${fmtMoney(deal.asking_price)}` }] : []),
        ...(deal.revenue != null ? [{ Icon: BarChart3, text: `Revenue ${fmtMoney(deal.revenue)}` }] : []),
        ...(deal.ebitda != null ? [{ Icon: Calculator, text: `EBITDA ${fmtMoney(deal.ebitda)}` }] : []),
      ]
    : [];

  return (
    <div className="public-stage pb-10">
      <div className="w-full px-4 py-8 sm:px-6 lg:px-8">
        <PageHeader />

        {/* Loading skeleton */}
        {loading && (
          <div className="overflow-hidden rounded-[1.35rem] border border-border/70 bg-card p-5 shadow-card sm:p-7">
            <div className="animate-pulse space-y-5">
              <div className="h-6 w-2/3 rounded-lg bg-muted" />
              <div className="h-40 rounded-2xl bg-muted" />
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-24 rounded-xl bg-muted odd:last:col-span-2 lg:odd:last:col-span-1" />
                ))}
              </div>
              <div className="h-12 rounded-lg bg-muted" />
              <div className="h-12 rounded-lg bg-muted" />
            </div>
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="rounded-[1.35rem] border border-border/70 bg-card p-10 text-center shadow-card">
            <p className="text-base text-muted-foreground">{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-semibold text-primary-foreground transition-[filter] hover:brightness-[1.06]"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        )}

        {/* No BuyBox saved yet */}
        {!loading && !error && data && !data.has_preferences && (
          <div className="rounded-[1.35rem] border border-border/70 bg-card p-10 text-center shadow-card">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
              <Sparkles className="h-7 w-7 text-primary" />
            </span>
            <h2 className="mt-4 font-display text-2xl font-extrabold text-foreground">
              Set up your BuyBox to unlock AI Deal Scoring
            </h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">
              Tell us your budget, target industries, preferred geography, and earnings floor —
              we'll score every active listing against it and surface your best matches.
            </p>
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              className="mt-5 inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06]"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Set Up My BuyBox
            </button>
          </div>
        )}

        {/* No active listings to score */}
        {!loading && !error && data?.has_preferences && deals.length === 0 && (
          <div className="rounded-[1.35rem] border border-border/70 bg-card p-10 text-center shadow-card">
            <h2 className="font-display text-2xl font-extrabold text-foreground">
              No active listings to score right now
            </h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">
              As soon as new deals go live they'll be scored against your BuyBox automatically —
              check back soon.
            </p>
          </div>
        )}

        {/* Scored deals */}
        {!loading && !error && data?.has_preferences && deal && (
          <>
            {data.buybox && <BuyBoxChips buybox={data.buybox} onEdit={() => setPrefsOpen(true)} />}

            <div ref={scorecardRef} className="public-listing-card scroll-mt-4 overflow-hidden rounded-[1.35rem] shadow-glow">
              {/* Deal selector row */}
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 px-5 py-4 sm:px-7">
                <div className="min-w-0">
                  <h2 className="truncate text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                    <span className="text-muted-foreground">Deal:</span> {deal.title}
                  </h2>
                  {facts.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                      {facts.map(({ Icon, text }) => (
                        <span key={text} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                          <Icon className="h-3.5 w-3.5" />
                          {text}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSwitcherOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition-[filter] hover:brightness-[1.06]"
                >
                  Switch Deal
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>

              {/* Body */}
              <div className="space-y-5 p-5 sm:p-7">
                {/* "Why this matches" banner — same Dark · Gold hero shell as the
                    page header, so the two gradients read as one system */}
                <div className="public-hero-shell rounded-2xl px-5 py-6 text-foreground shadow-glow sm:px-7">
                  <div className="public-grid-lattice absolute inset-0 opacity-45" />
                  <div className="pointer-events-none absolute -right-14 top-0 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
                  <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center">
                    <div className="flex shrink-0 flex-col items-center">
                      <FitScoreRing score={deal.fit_score} />
                      <span className="mt-2 text-base font-bold">{ratingLabel(deal.fit_score)}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="mb-4 text-xl font-bold">Why this matches your BuyBox</h3>
                      <ul className="grid grid-cols-1 gap-x-8 gap-y-2.5 sm:grid-cols-2">
                        {deal.signals.map((signal, i) => {
                          const { Icon, chip } = SIGNAL_STYLES[signal.kind];
                          return (
                            <li key={i} className="flex items-start gap-2.5 text-base text-foreground/90">
                              <span
                                className={cn(
                                  "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[0.35rem]",
                                  chip
                                )}
                              >
                                <Icon className="h-3.5 w-3.5 text-white" />
                              </span>
                              <span>{signal.text}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Metric tiles — score, label, and a mini progress bar. Five
                    metrics: on two columns the odd last tile spans the row. */}
                <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                  {deal.metrics.map((metric) => (
                    <div
                      key={metric.key}
                      className="odd:last:col-span-2 lg:odd:last:col-span-1 rounded-xl border border-border/70 bg-gradient-to-b from-muted/70 to-muted/25 px-4 py-5 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
                    >
                      <div className={cn("text-4xl font-extrabold tabular-nums", scoreTextColor(metric.value))}>
                        {metric.value}
                      </div>
                      <div className="mt-1.5 text-sm font-medium text-muted-foreground">{metric.label}</div>
                      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-border/50">
                        <div
                          className="h-full rounded-full transition-[width] duration-700 ease-out"
                          style={{ width: `${metric.value}%`, backgroundColor: ringStroke(metric.value) }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Insight rows */}
                <div className="space-y-2.5">
                  {deal.insights.map((insight, i) => {
                    const { Icon, row, icon } = INSIGHT_STYLES[insight.kind];
                    return (
                      <div
                        key={i}
                        className={cn("flex items-start gap-2.5 rounded-lg border px-4 py-3.5 text-base", row)}
                      >
                        <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", icon)} />
                        <span>{insight.text}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-1">
                  <Link
                    to={`/listings/${deal.listing_id}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-gold-gradient px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow transition-[filter] hover:brightness-[1.06]"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View Listing
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                  {/* Hidden for now: Open DD Checklist (/buyer/due-diligence) */}
                  <Link
                    to={orgScopedPath("/buyer/sba-calculator", user?.org_slug)}
                    className="inline-flex items-center gap-2 rounded-xl border border-border/80 bg-card px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:border-primary/30 hover:bg-accent/55"
                  >
                    <Calculator className="h-4 w-4" />
                    Run SBA Calculator
                  </Link>
                </div>
              </div>
            </div>

            {deals.length > 1 && (
              <OtherMatchesCarousel
                deals={deals.filter((d) => d.listing_id !== deal.listing_id)}
                onSelect={selectFromCarousel}
              />
            )}
          </>
        )}
      </div>

      {/* Deal switcher — centered popup ranking the top deals by BL Score */}
      {switcherOpen && deal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSwitcherOpen(false)} />
          <div className="relative z-10 flex max-h-[80dvh] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border/70 bg-card shadow-2xl">
            <header className="flex items-center justify-between border-b border-border/70 px-5 py-4">
              <div>
                <h3 className="font-display text-xl font-bold text-foreground">Your Top {deals.length} AI Deals</h3>
                <p className="mt-0.5 text-sm text-muted-foreground">Ranked by BL Score against your BuyBox</p>
              </div>
              <button
                type="button"
                onClick={() => setSwitcherOpen(false)}
                aria-label="Close deal switcher"
                className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </header>
            <div className="flex-1 space-y-2 overflow-y-auto p-3">
              {deals.map((d, i) => {
                const rowFacts = [
                  d.location,
                  d.asking_price != null ? `Asking ${fmtMoney(d.asking_price)}` : null,
                ].filter(Boolean).join(" · ");
                return (
                  <button
                    key={d.listing_id}
                    type="button"
                    onClick={() => selectDeal(d.listing_id)}
                    className={cn(
                      "flex w-full items-center gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors",
                      d.listing_id === deal.listing_id
                        ? "border-primary/50 bg-primary/10"
                        : "border-border/60 hover:border-primary/30 hover:bg-accent/40"
                    )}
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
                      {i + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-base font-semibold text-foreground">{d.title}</div>
                      {rowFacts && <div className="mt-0.5 truncate text-sm text-muted-foreground">{rowFacts}</div>}
                    </div>
                    <span
                      className={cn(
                        "flex shrink-0 flex-col items-center rounded-lg border px-2.5 py-1",
                        SCORE_CHIP_CLASSES
                      )}
                    >
                      <span className="text-lg font-extrabold leading-none tabular-nums">{d.fit_score}</span>
                      <span className="mt-0.5 text-[0.55rem] font-bold uppercase tracking-[0.1em] opacity-80">
                        BL Score
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Edit / set up BuyBox — refetch scores once preferences are saved */}
      {prefsOpen && (
        <BuyerPreferencesModal
          forceOpen
          onClose={() => {
            setPrefsOpen(false);
            void load();
          }}
        />
      )}
    </div>
  );
}
